/* oxlint-disable no-static-element-interactions -- disabled because focus behavior is intentional */
import { autoPlacement, autoUpdate, size as floatingResize, useFloating } from '@floating-ui/react';
import clsx from 'clsx';
import { useCombobox } from 'downshift';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { IntlProvider } from '../../../contexts/IntlProvider';
import cs from '../../../locales/cs.json';
import de from '../../../locales/de.json';
import en from '../../../locales/en.json';
import es from '../../../locales/es.json';
import fr from '../../../locales/fr.json';
import it from '../../../locales/it.json';
import pt from '../../../locales/pt.json';
import zh from '../../../locales/zh.json';
import { createFilter } from '../../../useFilters';
import { isDefined } from '../../../utils/typescript-helpers';
import { SgComponentIcon } from '../../display/ComponentIcon';
import { SgFormattedMessage } from '../../SgFormattedMessage';
import { ClearButton } from './ClearButton';
import { SgPickerInputProvider } from './context';
import { DropdownIndicator, LoadingIndicator } from './DropdownIndicator';
import { SgPickerMenu } from './DropdownMenu';
import { useTruncateBadges } from './hooks/useTruncateBadges';
import { Input } from './Input';
import { SelectedBadges } from './SelectedBadges';
import { SummaryLabel } from './SummaryLabel';
import type { SgPickerItem, SgPickerProps } from './types';

import './index.css';

/**
 * SgPicker - A versatile picker component supporting both single and multiple selection modes
 *
 * Automatically infers the type of item values from the items prop.
 * - If items have string values, all callbacks will be typed with string
 * - If items have number values, all callbacks will be typed with number
 */

export function SgPicker<T extends string | number>(props: SgPickerProps<T>): React.ReactElement {
  const {
    'data-e2e': dataE2E,
    itemSelection = 'multiple',
    selectedItemsDisplay = itemSelection === 'multiple' ? 'badges' : undefined,
    selectedValues: controlledSelectedValues,
    inputValue: controlledInputValue,
    defaultSelectedValues,
    defaultInputValue,
    items = [],
    onChange,
    purpose = 'default',
    classes = '',
    inputId,
    inputHeight,
    labelledBy,
    inputIcon = 'search',
    showIcon = true,
    showDropdownIndicator = true,
    showNoResultLabel = true,
    showTypingHintLabel = true,
    showClearButton = true,
    size = 'md',
    placeholder,
    disabled,
    readonly,
    color = 'info',
    dropdownPlacement = 'bottom',
    height = 250,
    dropdownParent,
    isOpen: controlledIsOpen,
    maxBadges = 5,
    canSelectAll = false,
    isLoading = false,
    hasTooManyResults = false,
    showInputAsCustomItem = false,
    createCustomItemButtonLabel = 'Create',
    onCreateCustomItem,
    onInputChanged,
    onInputCleared,
    onPaste,
    onBackspaceRemove,
    onClear,
    onSelectAll,
    onUnselectAll,
    onBlurInput,
    onFocus,
    onClick,
    onIsOpenChange,
    validationState,
    validationMessage,
    hasReachLimit,
    inputRef,
    dropdownRef,
  } = props;

  /** Create internal refs if not provided */
  const internalInputRef = useRef<HTMLInputElement | null>(null);
  const internalDropdownRef = useRef<HTMLDivElement | null>(null);

  /** Use provided refs or fallback to internal ones */
  const actualInputRef = inputRef || internalInputRef;
  const actualDropdownRef = dropdownRef || internalDropdownRef;

  /**
   * CONTROLLED vs UNCONTROLLED STATE MANAGEMENT
   * ==========================================
   *
   * This component supports both controlled and uncontrolled modes:
   * - Controlled: Parent manages selectedValues/inputValue via props
   * - Uncontrolled: Component manages its own internal state
   *
   * The component auto-detects the mode based on prop presence.
   */
  const isSelectedValuesControlled = isDefined(controlledSelectedValues);
  const isInputControlled = isDefined(controlledInputValue);

  /** Development warnings to prevent common controlled/uncontrolled conflicts */
  if (isDefined(controlledInputValue) && isDefined(defaultInputValue)) {
    console.warn(
      '🚧 Setting `defaultInputValue` has no effect on controlled input mode - Check docs ## Controlled vs Uncontrolled',
    );
  }

  if (isDefined(defaultSelectedValues) && isDefined(controlledSelectedValues)) {
    console.warn(
      '🚧 Setting `defaultSelectedValues` has no effect on controlled selection mode - Check docs ## Controlled vs Uncontrolled',
    );
  }

  /**
   * Internal state for selected values (uncontrolled mode only).
   * Initialized with controlled values if present, otherwise defaults.
   */
  const [internalSelectedValues, setInternalSelectedValues] = useState<(string | number)[]>(() => {
    return isSelectedValuesControlled ? controlledSelectedValues : (defaultSelectedValues ?? []);
  });

  /**
   * Internal state for input value (uncontrolled mode only).
   * Always initialized with defaultInputValue to avoid controlled/uncontrolled switching.
   */
  const [internalInputValue, setInternalInputValue] = useState(() => {
    return defaultInputValue || '';
  });

  const [isFocused, setIsFocused] = useState(false);
  const [isHover, setIsHover] = useState(false);

  /**
   * COMPUTED STATE VALUES
   * ====================
   *
   * These values are computed from either controlled props or internal state,
   * providing a unified interface regardless of control mode.
   */
  const selectedValues = isSelectedValuesControlled ? controlledSelectedValues : internalSelectedValues;
  const inputValue = isInputControlled ? controlledInputValue : internalInputValue;

  /** Transform selected values into selected item objects for easy access */
  const selectedItems = useMemo(
    () => items.filter((item) => selectedValues.includes(item.value)),
    [items, selectedValues],
  );

  /** In single selection mode, provide easy access to the selected item */
  const selectedItem = useMemo(
    () => (itemSelection === 'single' ? selectedItems[0] || null : null),
    [itemSelection, selectedItems],
  );

  /**
   * TRUNCATE BADGES MODE
   * ====================
   *
   * Hook pour gérer le mode truncate qui affiche dynamiquement
   * le nombre de badges qui peuvent tenir sur une ligne
   */
  const {
    wrapperRef: pickerWrapperRef,
    badgesContainerRef,
    visibleBadgesCount,
  } = useTruncateBadges(selectedItems.length, selectedItemsDisplay, inputHeight);

  /**
   * ITEM FILTERING LOGIC
   * ===================
   *
   * Implements intelligent filtering based on input value:
   * - Empty input: shows all items
   * - Controlled mode: assumes parent handles filtering, skip default filtering
   * - Default behavior: applies 'contains' filtering using createFilter utility
   */
  const filteredItems = useMemo(() => {
    if (!inputValue.trim()) {
      return items;
    }

    // In controlled mode, assume parent handles filtering
    if (isSelectedValuesControlled) {
      return items;
    }

    // Default filtering behavior for uncontrolled mode
    const filter = createFilter();
    return items.filter((item) => filter.contains(item.label, inputValue));
  }, [items, inputValue, isSelectedValuesControlled]);

  /**
   * ITEM SORTING LOGIC
   * ==================
   *
   * Implements smart sorting to improve UX:
   * - In multiple selection + uncontrolled mode: selected items appear first
   * - Otherwise: maintains original order from props
   * - Helps users see their selections at the top of the dropdown
   */
  const sortedItems = useMemo(() => {
    // Only sort in uncontrolled mode and multiple selection
    if (isSelectedValuesControlled || itemSelection !== 'multiple') {
      return filteredItems;
    }

    // Create a copy and sort: selected items first, then unselected items
    return filteredItems.toSorted((a, b) => {
      const aIsSelected = selectedValues.includes(a.value);
      const bIsSelected = selectedValues.includes(b.value);

      // Maintain original order within same selection status
      if (aIsSelected === bIsSelected) {
        return 0;
      }

      // Selected items come first
      return aIsSelected ? -1 : 1;
    });
  }, [filteredItems, selectedValues, isSelectedValuesControlled, itemSelection]);

  /**
   * AUTO-FILL INPUT EFFECT
   * ======================
   *
   * In single selection uncontrolled mode, automatically displays
   * the selected item's label in the input for better UX.
   */
  useEffect(() => {
    if (isInputControlled) {
      return;
    }

    if (selectedItem && itemSelection === 'single') {
      const label = typeof selectedItem.label === 'string' ? selectedItem.label : '';
      setInternalInputValue(label);
    }
  }, [selectedItem, itemSelection, isInputControlled]);

  /**
   * Checks if all filtered items are currently selected
   * @returns {boolean} True if all selectable filtered items are selected
   */
  const areAllFilteredItemsSelected = (): boolean => {
    const selectableItems = sortedItems.filter((item) => !item.disabled);
    return selectableItems.length > 0 && selectableItems.every((item) => selectedValues.includes(item.value));
  };

  /**
   * Gets all selectable items from the current filtered/sorted items
   * @returns {SgPickerItem[]} Array of selectable items (non-disabled)
   */
  const getSelectableItems = (): SgPickerItem[] => {
    return sortedItems.filter((item) => !item.disabled);
  };

  /**
   * Handles the creation of custom items when the create button is clicked
   * @param item - The create custom item button
   */
  const handleCustomItemCreation = (item: SgPickerItem<T> & { isCreateCustomItem?: boolean }): void => {
    if (item.isCreateCustomItem) {
      createNewItem(inputValue);
    }
  };

  /**
   * Handles select all/unselect all functionality
   * @param item - The select all button item
   */
  const handleSelectAllToggle = (item: SgPickerItem<T> & { isSelectAllButton?: boolean }): void => {
    if (!item.isSelectAllButton) {
      return;
    }

    const isSelectAll = !areAllFilteredItemsSelected();
    const selectableItems = getSelectableItems();

    // Controlled mode - delegate to parent callbacks
    if (isSelectedValuesControlled) {
      if (isSelectAll && onSelectAll) {
        onSelectAll();
      } else if (!isSelectAll && onUnselectAll) {
        onUnselectAll();
      } else {
        // Fallback: call onChange for each item
        if (isSelectAll) {
          selectableItems.forEach((item) => {
            if (!selectedValues.includes(item.value)) {
              onChange?.(item as SgPickerItem<T>);
            }
          });
        } else {
          onClear?.();
        }
      }
      return;
    }

    // Uncontrolled mode - manage internal state
    if (isSelectAll) {
      const newSelectedValues = [...selectedValues];
      selectableItems.forEach((item) => {
        if (!newSelectedValues.includes(item.value)) {
          newSelectedValues.push(item.value);
        }
      });
      setInternalSelectedValues(newSelectedValues);

      // Notify parent of newly selected items
      selectableItems.forEach((item) => {
        if (!selectedValues.includes(item.value)) {
          onChange?.(item as SgPickerItem<T>);
        }
      });
    } else {
      setInternalSelectedValues([]);
      onClear?.();
    }
  };

  /**
   * Handles regular item selection/deselection
   * @param item - The regular picker item to select/deselect
   */
  const handleRegularItemSelection = (item: SgPickerItem<T>): void => {
    // Notify parent component
    onChange?.(item as SgPickerItem<T>);

    // Update internal state for uncontrolled components
    if (!isSelectedValuesControlled) {
      if (itemSelection === 'single') {
        setInternalSelectedValues([item.value]);
      } else {
        // Toggle selection for multiple mode
        setInternalSelectedValues((prev) => {
          const isSelected = prev.includes(item.value);
          return isSelected ? prev.filter((value) => value !== item.value) : [...prev, item.value];
        });
      }
    }

    // Clean up input value after selection in uncontrolled mode (except single mode)
    // In single mode, the useEffect will auto-fill the selected item's label
    // In controlled mode, let the user manage the input value
    if (!isInputControlled && itemSelection === 'multiple') {
      setInternalInputValue('');
    }

    // Manage input focus for single selection mode
    if (itemSelection === 'single' && actualInputRef.current) {
      actualInputRef.current.blur();
    }
  };

  /**
   * Handles item selection changes in the picker component.
   *
   * This is the main entry point for all selection events. It routes different
   * types of selections to specialized handlers:
   * - Disabled items: Prevented from selection
   * - Custom item creation: Routes to creation handler
   * - Select all/unselect all: Routes to bulk selection handler
   * - Regular items: Routes to standard selection handler
   *
   * @param item - The picker item being interacted with
   */
  const handleSelectionChange = (item: SgPickerItem) => {
    // Early return for disabled items
    if (item.disabled) {
      return;
    }

    const extendedItem = item as SgPickerItem<T> & {
      isCreateCustomItem?: boolean;
      isSelectAllButton?: boolean;
    };

    // Route to appropriate handler based on item type
    if (extendedItem.isCreateCustomItem) {
      handleCustomItemCreation(extendedItem);
    } else if (extendedItem.isSelectAllButton) {
      handleSelectAllToggle(extendedItem);
    } else {
      handleRegularItemSelection(item as SgPickerItem<T>);
    }
  };

  /**
   * Floating UI configuration for dropdown positioning
   *
   * Features:
   * - Auto-placement when placement is 'auto'
   * - Dynamic sizing based on reference element width
   * - Height constraints based on available space
   * - Auto-update positioning on scroll/resize
   */
  const { refs, floatingStyles } = useFloating({
    placement: dropdownPlacement === 'auto' ? undefined : dropdownPlacement === 'top' ? 'top' : 'bottom',
    whileElementsMounted: autoUpdate,
    middleware: [
      floatingResize({
        apply({ availableHeight, elements, rects }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${rects.reference.width}px`,
            maxHeight: `${Math.min(height, availableHeight)}px`,
          });
        },
      }),
      dropdownPlacement === 'auto' &&
        autoPlacement({
          allowedPlacements: ['top', 'bottom'],
        }),
    ].filter(Boolean),
  });

  // Custom item creation handler
  const createNewItem = async (label: string) => {
    if (!isSelectedValuesControlled) {
      console.warn('🚧 `onCreateCustomItem` is only available in controlled selection mode.');
      return;
    }
    if (!onCreateCustomItem || !showInputAsCustomItem) {
      return;
    }
    onCreateCustomItem(label);
  };

  const canCreateCustomItem = showInputAsCustomItem && inputValue.trim().length > 0;

  // Create extended items list that includes the custom item creation option and select all button
  const extendedItems = useMemo(() => {
    const baseItems = [...sortedItems];

    // Add virtual item for custom item creation if enabled - at the beginning for first keyboard selection
    if (canCreateCustomItem && !isLoading) {
      baseItems.unshift({
        value: `__CREATE_CUSTOM_ITEM__${inputValue}` as T,
        label: inputValue,
        isCreateCustomItem: true,
      } as SgPickerItem<T> & { isCreateCustomItem: boolean });
    }

    // Add select all button for multiple selection mode
    if (itemSelection === 'multiple' && canSelectAll && baseItems.filter((item) => !item.disabled).length > 0) {
      baseItems.unshift({
        value: '__SELECT_ALL__' as T,
        label: 'Select All',
        isSelectAllButton: true,
      } as SgPickerItem<T> & { isSelectAllButton: boolean });
    }

    return baseItems;
  }, [sortedItems, canCreateCustomItem, isLoading, inputValue, itemSelection, canSelectAll]);

  /**
   * Downshift combobox configuration for keyboard navigation and accessibility
   *
   * Handles:
   * - Input value changes with debounced search
   * - Item selection in both single and multiple modes
   * - Keyboard navigation (arrow keys, enter, escape)
   * - Accessibility attributes (aria-*)
   */
  const { isOpen, getInputProps, getMenuProps, getItemProps, getToggleButtonProps, highlightedIndex } =
    useCombobox<SgPickerItem>({
      inputValue,
      items: extendedItems,
      // Use controlled isOpen if provided, otherwise let downshift manage it internally
      // Force isOpen to false when readonly is true
      isOpen: readonly ? false : controlledIsOpen,
      // In 'single' mode: Downshift needs to know the selected item for highlighting and navigation
      // In 'multiple' mode: No single selected item, so null
      selectedItem: itemSelection === 'single' ? selectedItem : null,
      // Automatically highlight the first item when dropdown opens
      defaultHighlightedIndex: 0,
      // For items with JSX labels, we use a simple string conversion
      itemToString: (item) => {
        if (!item) {
          return '';
        }

        // Handle custom creation item specially
        const extendedItem = item as SgPickerItem & { isCreateCustomItem?: boolean };
        if (extendedItem.isCreateCustomItem) {
          return ''; // Return empty string to avoid filtering issues
        }

        // If label is a string, return it directly
        const label = item.label;
        if (typeof label === 'string') {
          return label;
        }

        // If label isn't string, we can't use it for filtering
        return '';
      },
      /**
       * Handles changes to the input value.
       *
       * Special handling for:
       * - Input changes: updates inputValue state and notifies parent component
       * - Arrow key navigation: updates highlightedIndex for dropdown menu
       * @param param0 - The state change parameters.
       */
      onInputValueChange: ({ inputValue: newInputValue, type }) => {
        if (
          type === useCombobox.stateChangeTypes.InputChange ||
          type === useCombobox.stateChangeTypes.InputKeyDownArrowDown ||
          type === useCombobox.stateChangeTypes.InputKeyDownArrowUp
        ) {
          // Only set input value when user types
          const value = newInputValue ?? '';

          // Update internal value if not controlled

          if (!isInputControlled) {
            setInternalInputValue(value);
          }

          // Notify parent component with debounce
          onInputChanged?.(value);

          // If user clears input in single mode, deselect the item
          if (itemSelection === 'single' && value === '' && selectedItem) {
            handleSelectionChange(selectedItem);
          }
        }
      },
      onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
        if (!newSelectedItem) {
          return;
        }

        handleSelectionChange(newSelectedItem);
      },
      onIsOpenChange: ({ isOpen: newIsOpen }) => {
        // Prevent opening when readonly
        if (readonly && newIsOpen) {
          return;
        }
        onIsOpenChange?.(newIsOpen ?? false);
      },
    });

  // Clear button handler
  const handleClear = () => {
    if (!isInputControlled) {
      setInternalInputValue('');
    }

    // In uncontrolled mode, also clear selections
    if (!isSelectedValuesControlled) {
      setInternalSelectedValues([]);
    }

    // Notify parent about input being cleared
    onInputCleared?.();
    onClear?.();
  };

  // Remove a selected item (used in multiple selection mode)
  const removeSelectedItem = (itemToRemove: SgPickerItem) => {
    handleSelectionChange(itemToRemove);
  };

  // Handle backspace removal of last selected item in multiple mode
  const handleBackspaceRemove = () => {
    if (itemSelection === 'multiple' && selectedItems.length > 0 && inputValue.trim() === '') {
      const lastSelectedItem = selectedItems[selectedItems.length - 1];
      if (lastSelectedItem) {
        // Remove the item using the same logic as selection change
        handleSelectionChange(lastSelectedItem);
        // Call the callback if provided
        onBackspaceRemove?.(lastSelectedItem);
      }
    }
  };

  const locales = {
    en,
    fr,
    de,
    es,
    it,
    pt,
    cs,
    zh,
  };

  // Render
  return (
    <IntlProvider locales={locales} defaultLocale="en">
      <SgPickerInputProvider
        value={{
          // Core props
          color,
          purpose,
          size,
          inputValue,
          canCreateCustomItem,
          isLoading,
          showNoResultLabel,
          showTypingHintLabel,
          onChange: handleSelectionChange,

          // Menu props
          isOpen,
          items: extendedItems,
          itemSelection,
          selectedItems,
          selectedItem,
          highlightedIndex,
          getMenuProps,
          getItemProps,
          getToggleButtonProps,
          refs,
          floatingStyles,
          canSelectAll,
          hasTooManyResults,
          onSelectAll,
          onUnselectAll,

          // Input props
          placeholder,
          disabled,
          readonly,
          inputId,
          labelledBy,
          getInputProps,
          onPaste,
          onBlurInput,
          onFocus,
          onBackspaceRemove: handleBackspaceRemove,
          setFocused: setIsFocused,

          // Clear button props
          showClearButton,
          onClear: handleClear,

          // Selected badges props
          removeSelectedItem,

          // UI state
          isFocused,
          isHover,
          showInputAsCustomItem,
          createCustomItemButtonLabel,
          createCustomItemHandler: createNewItem,

          // Refs for external access
          inputRef: actualInputRef,
          dropdownRef: actualDropdownRef,
        }}
      >
        <div className={clsx('position-relative overflow-hidden', classes)} onClick={onClick} data-e2e={dataE2E}>
          <div ref={refs.setReference} className={`flex-column input-group w-100 input-group-${size}`}>
            <div
              onMouseEnter={() => setIsHover(true)}
              onMouseLeave={() => setIsHover(false)}
              style={{
                ...(isOpen || isFocused
                  ? {
                      boxShadow: 'inset 0 0 0 2px var(--bs-primary, var(--primary)) !important',
                    }
                  : {}),
                ...(disabled
                  ? {
                      backgroundColor: 'transparent',
                      opacity: 0.4,
                      boxShadow: 'inset 0 -0.0625rem 0 var(--bs-border-color, var(--border-color))',
                    }
                  : {}),
              }}
              ref={pickerWrapperRef}
              className={clsx(
                'w-100 align-items-stretch py-0 pe-0 pr-0 form-control',
                purpose === 'outline' ? 'form-control-outline form-control-alt' : '',
                'd-flex h-auto align-items-center',
                disabled && 'disabled',
                readonly && 'readonly',
                isDefined(validationState) && `is-${validationState}`,
                hasReachLimit && 'is-invalid',
              )}
            >
              <label
                htmlFor={inputId}
                onClick={(e) => {
                  // Prevent default label click behavior
                  // That conflict when we have badges with click events
                  e.stopPropagation();
                  e.preventDefault();
                }}
                className={'overflow-hidden d-flex flex-wrap flex-grow-1 justify-content-start align-items-start mb-0'}
              >
                <div className="h-100 w-100 d-flex justify-content-start align-items-center">
                  {/** Display icon if enabled */}
                  {showIcon && (
                    <div {...(disabled || readonly ? {} : getToggleButtonProps())}>
                      <SgComponentIcon
                        className="me-2 icon text-secondary d-flex align-items-center flex-shrink-0"
                        size={size}
                        icon={inputIcon}
                      />
                    </div>
                  )}

                  {/* Selected item badges and input in multiple selection mode - with flex wrap */}
                  <div
                    /* container that holds the scrollable badges area + the input row */
                    ref={pickerWrapperRef}
                    className={'d-flex flex-column me-2 flex-grow-1'}
                    style={{
                      minHeight: 0,
                      height: '100%',
                    }}
                  >
                    {/* scrollable badges area */}
                    <div
                      ref={badgesContainerRef}
                      className={`${selectedItemsDisplay === 'truncate' ? 'd-flex flex-nowrap' : 'd-flex flex-wrap'} align-items-center`}
                      style={{
                        overflowY: inputHeight ? 'auto' : undefined,
                        overflowX: selectedItemsDisplay === 'truncate' ? 'auto' : undefined,
                        flex: 1,
                        minHeight: 0,
                      }}
                    >
                      {itemSelection === 'multiple' &&
                        (selectedItemsDisplay === 'count' ? (
                          <SummaryLabel color={color} selectedItems={selectedItems} />
                        ) : selectedItemsDisplay === 'truncate' ? (
                          <>
                            {selectedItems?.map((item, index) => {
                              const isVisible = index < visibleBadgesCount;
                              const measureStyle: React.CSSProperties = isVisible
                                ? {}
                                : {
                                    position: 'absolute',
                                    visibility: 'hidden',
                                    pointerEvents: 'none',
                                    left: '-9999px',
                                    top: '0',
                                  };

                              return (
                                <span key={String(item?.value)} style={measureStyle}>
                                  <SelectedBadges
                                    item={item}
                                    isLast={
                                      index === visibleBadgesCount - 1 && visibleBadgesCount === selectedItems.length
                                    }
                                  />
                                </span>
                              );
                            })}
                            {visibleBadgesCount < selectedItems?.length && (
                              <span
                                className={`bg-lvl1 item px-8px flex-center badge badge-dismissible badge-default sgbs-badge-default mt-1 badge-${size} me-12px`}
                              >
                                +{selectedItems?.length - visibleBadgesCount}
                              </span>
                            )}
                            <Input data-e2e={dataE2E} />
                          </>
                        ) : maxBadges && selectedItems?.length > maxBadges ? (
                          <>
                            {selectedItems?.slice(0, maxBadges).map((item) => (
                              <SelectedBadges key={item?.value} item={item} />
                            ))}
                            <span
                              className={`bg-lvl1 item px-8px flex-center badge badge-dismissible badge-default sgbs-badge-default mt-1 badge-${size} me-12px`}
                            >
                              +{selectedItems?.length - maxBadges}
                            </span>
                            <Input data-e2e={dataE2E} />
                          </>
                        ) : (
                          <>
                            {selectedItems.map((item, index) => (
                              <SelectedBadges
                                key={item?.value}
                                item={item}
                                isLast={index === selectedItems?.length - 1}
                              />
                            ))}
                            <Input data-e2e={dataE2E} />
                          </>
                        ))}

                      {/* Single selection mode input */}
                      {itemSelection === 'single' && <Input data-e2e={dataE2E} />}
                    </div>
                  </div>
                </div>
              </label>
              <div className="d-flex align-items-center">
                {showClearButton && <ClearButton />}

                {!isLoading && showDropdownIndicator && <DropdownIndicator />}

                {isLoading && <LoadingIndicator />}
              </div>
            </div>
          </div>

          {/* Validation message */}
          {validationMessage && validationState && (
            <div
              className={clsx(
                'd-block',
                validationState === 'invalid' && 'invalid-feedback text-invalid',
                validationState === 'valid' && 'valid-feedback text-valid',
                validationState === 'warning' && 'text-warning',
                validationState === 'edited' && 'text-edited',
              )}
            >
              {validationMessage}
            </div>
          )}

          {/* Reach limit */}
          {hasReachLimit && (
            <div className={'invalid-feedback d-block'}>
              <SgFormattedMessage id="picker.limit" />
            </div>
          )}
          {/* Dropdown menu — use React.createPortal (synchronous) instead of FloatingPortal
           * (which defers rendering via useLayoutEffect). This ensures SgPickerMenu always
           * renders on the first React render, so downshift's getMenuProps validation
           * useEffect([]) finds the getter already called and suppressed.
           */}
          {createPortal(<SgPickerMenu data-e2e={dataE2E} />, dropdownParent ?? document.body)}
        </div>
      </SgPickerInputProvider>
    </IntlProvider>
  );
}
